/**
 * Review Cards Masonry Layout with Filtering
 * Handles responsive masonry layout and category filtering for review cards
 * @fileoverview TypeScript checking disabled for DOM manipulation
 */
// @ts-nocheck
class ReviewMasonry {
  constructor() {
    // DOM elements
    this.filterBtns = document.querySelectorAll(".filter-btn");
    this.reviews = document.querySelectorAll(".review-card");
    this.reviewsGrid = document.querySelector(".reviews-grid");

    // Configuration
    this.config = {
      desktopColumns: 4,
      mobileBreakpoint: 749,
      gap: 16,
      resizeDelay: 50, // Reduced for more responsive layout
      layoutDelay: 10, // Reduced delay for faster response
      debounceDelay: 100, // New: prevent layout call stacking
    };

    // State
    this.state = {
      currentFilter: "all",
      columnHeights: [],
      isMobile: false,
      isInitialized: false,
      isLayouting: false, // Prevent overlapping layout calls
      layoutRequestId: null, // Store animation frame ID
      resizeTimeout: null, // Store resize timeout ID
      mutationTimeout: null, // Store mutation observer timeout ID
    };

    this.init();
  }

  /**
   * Initialize the masonry layout and event listeners
   */
  init() {
    this.bindEvents();
    this.setupMutationObserver();
    this.scheduleLayout();
    this.state.isInitialized = true;
  }

  /**
   * Setup mutation observer to watch for DOM changes
   */
  setupMutationObserver() {
    if (!window.MutationObserver || !this.reviewsGrid) return;
    
    const observer = new MutationObserver(() => {
      // Debounce rapid mutations
      if (this.state.mutationTimeout) {
        clearTimeout(this.state.mutationTimeout);
      }
      
      this.state.mutationTimeout = setTimeout(() => {
        this.scheduleLayout();
      }, 100);
    });
    
    observer.observe(this.reviewsGrid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Filter buttons
    this.filterBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => this.handleFilterClick(e));
    });

    // Window resize with improved handling
    window.addEventListener("resize", () => {
      // Clear previous resize timeout
      if (this.state.resizeTimeout) {
        clearTimeout(this.state.resizeTimeout);
      }
      
      // Cancel any pending layout requests
      if (this.state.layoutRequestId) {
        cancelAnimationFrame(this.state.layoutRequestId);
        this.state.layoutRequestId = null;
      }
      
      // Immediately reset styles to prevent overlapping during resize
      this.immediateReset();
      
      this.state.resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, this.config.resizeDelay);
    });
  }

  /**
   * Handle filter button clicks
   */
  handleFilterClick(event) {
    const btn = event.target;
    if (!btn || !btn.getAttribute) return;
    
    const filterData = btn.getAttribute("data-filter");
    if (!filterData) return;
    
    const selectedCategory = filterData.toLowerCase();

    // Update button states
    this.updateActiveButton(btn);

    // Update filter state and apply
    this.state.currentFilter = selectedCategory;
    this.applyFilter();
  }

  /**
   * Update active button state
   */
  updateActiveButton(activeBtn) {
    if (!activeBtn || !activeBtn.classList) return;
    
    this.filterBtns.forEach((btn) => {
      if (btn && btn.classList) {
        btn.classList.remove("active");
      }
    });
    activeBtn.classList.add("active");
  }

  /**
   * Apply current filter to reviews
   */
  applyFilter() {
    this.reviews.forEach((review) => {
      const shouldShow = this.shouldShowReview(review);
      this.toggleReview(review, shouldShow);
    });

    // Update layout after filter change
    this.scheduleLayout();
  }

  /**
   * Check if review should be shown based on current filter
   */
  shouldShowReview(review) {
    if (!review || !review.getAttribute) return false;
    if (this.state.currentFilter === "all") return true;

    const reviewCategories = review.getAttribute("data-categories");
    if (!reviewCategories) return false;
    
    return reviewCategories.toLowerCase().includes(this.state.currentFilter);
  }

  /**
   * Show or hide a review
   */
  toggleReview(review, show) {
    if (!review || !review.style || !review.classList) return;
    
    if (show) {
      review.style.display = "flex";
      review.classList.remove("hidden");
    } else {
      review.style.display = "none";
      review.classList.add("hidden");
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    this.resetCardStyles();
    this.scheduleLayout();
  }

  /**
   * Immediately reset card positions to prevent overlapping during resize
   */
  immediateReset() {
    if (!this.reviewsGrid) return;
    
    // Add resizing class for visual feedback
    this.reviewsGrid.classList.add('resizing');
    
    // Force all cards to relative positioning immediately
    this.reviews.forEach((review) => {
      if (review && review.style) {
        review.style.position = "relative";
        review.style.left = "auto";
        review.style.top = "auto";
        review.style.width = "auto";
      }
    });
    
    // Reset container height
    this.reviewsGrid.style.height = "auto";
    
    // Remove resizing class after a short delay
    setTimeout(() => {
      if (this.reviewsGrid) {
        this.reviewsGrid.classList.remove('resizing');
      }
    }, 100);
  }

  /**
   * Schedule a layout update using requestAnimationFrame for smooth performance
   */
  scheduleLayout() {
    // Prevent multiple layout calls from stacking
    if (this.state.isLayouting) {
      return;
    }

    // Cancel any pending layout
    if (this.state.layoutRequestId) {
      cancelAnimationFrame(this.state.layoutRequestId);
    }

    this.state.layoutRequestId = requestAnimationFrame(() => {
      this.state.isLayouting = true;
      
      // Force a reflow to ensure all elements have updated dimensions
      if (this.reviewsGrid && this.reviewsGrid.offsetHeight !== undefined) {
        this.reviewsGrid.offsetHeight;
      }
      
      this.updateLayout();
      
      this.state.isLayouting = false;
      this.state.layoutRequestId = null;
    });
  }

  /**
   * Reset all card styles to prepare for layout
   */
  resetCardStyles() {
    // Reset container height first
    if (this.reviewsGrid && this.reviewsGrid.style) {
      this.reviewsGrid.style.height = "";
    }
    
    this.reviews.forEach((review) => {
      if (!review || !review.style) return;
      
      // Only reset positioning for visible cards
      if (review.style.display !== "none") {
        review.style.position = "";
        review.style.left = "";
        review.style.top = "";
        review.style.width = "";
      }
    });
    
    // Force a reflow to ensure styles are applied
    if (this.reviewsGrid && this.reviewsGrid.offsetHeight !== undefined) {
      this.reviewsGrid.offsetHeight;
    }
  }

  /**
   * Update the layout based on screen size
   */
  updateLayout() {
    this.state.isMobile = window.innerWidth <= this.config.mobileBreakpoint;

    if (this.state.isMobile) {
      this.layoutMobile();
    } else {
      this.layoutDesktop();
    }
  }

  /**
   * Layout for mobile devices (single column)
   */
  layoutMobile() {
    let totalHeight = 0;

    this.reviews.forEach((review) => {
      if (!review || !review.style) return;
      if (review.style.display === "none") return;

      // Reset to mobile layout
      review.style.position = "relative";
      review.style.left = "0";
      review.style.top = "0";
      review.style.width = "100%";

      // Calculate total height
      if (review.offsetHeight !== undefined) {
        totalHeight += review.offsetHeight + this.config.gap;
      }
    });

    // Set container height
    if (this.reviewsGrid && this.reviewsGrid.style) {
      this.reviewsGrid.style.height = `${totalHeight}px`;
    }
  }

  /**
   * Layout for desktop (masonry)
   */
  layoutDesktop() {
    const visibleReviews = this.getVisibleReviews();
    if (visibleReviews.length === 0) return;

    // Calculate dimensions
    if (!this.reviewsGrid || this.reviewsGrid.offsetWidth === undefined) return;
    
    const containerWidth = this.reviewsGrid.offsetWidth;
    const cardWidth = this.calculateCardWidth(containerWidth);

    // Initialize columns
    this.state.columnHeights = new Array(this.config.desktopColumns).fill(0);

    // Position each card
    visibleReviews.forEach((review) => {
      this.positionCard(review, cardWidth);
    });

    // Set container height
    const maxHeight = Math.max(...this.state.columnHeights);
    if (this.reviewsGrid && this.reviewsGrid.style) {
      this.reviewsGrid.style.height = `${maxHeight}px`;
    }
  }

  /**
   * Get all visible reviews
   */
  getVisibleReviews() {
    return Array.from(this.reviews).filter(
      (review) => review && review.style && review.style.display !== "none"
    );
  }

  /**
   * Calculate card width for desktop layout
   */
  calculateCardWidth(containerWidth) {
    if (typeof containerWidth !== 'number' || containerWidth <= 0) return 0;
    
    const totalGaps = this.config.gap * (this.config.desktopColumns - 1);
    return (containerWidth - totalGaps) / this.config.desktopColumns;
  }

  /**
   * Position a single card in the masonry layout
   */
  positionCard(review, cardWidth) {
    if (!review || !review.style || typeof cardWidth !== 'number') return;
    
    // Set card properties
    review.style.position = "absolute";
    review.style.width = `${cardWidth}px`;

    // Force reflow to get accurate dimensions after width change
    if (review.offsetHeight !== undefined) {
      review.offsetHeight;
    }

    // Find shortest column
    const shortestColumnIndex = this.getShortestColumnIndex();
    if (shortestColumnIndex < 0 || shortestColumnIndex >= this.state.columnHeights.length) return;

    // Calculate position with proper precision
    const left = Math.round(shortestColumnIndex * (cardWidth + this.config.gap));
    const currentColumnHeight = this.state.columnHeights[shortestColumnIndex] || 0;
    const top = Math.round(currentColumnHeight);

    // Apply position
    review.style.left = `${left}px`;
    review.style.top = `${top}px`;

    // Get accurate height after positioning
    const cardHeight = review.offsetHeight || 0;
    
    // Update column height with accurate measurements
    this.state.columnHeights[shortestColumnIndex] = currentColumnHeight + cardHeight + this.config.gap;
  }

  /**
   * Find the index of the shortest column
   */
  getShortestColumnIndex() {
    if (!this.state.columnHeights || this.state.columnHeights.length === 0) return 0;
    
    const minHeight = Math.min(...this.state.columnHeights);
    return this.state.columnHeights.indexOf(minHeight);
  }

  /**
   * Public method to refresh layout (useful for external calls)
   */
  refresh() {
    this.resetCardStyles();
    this.scheduleLayout();
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  if (typeof window !== 'undefined') {
    window.reviewMasonry = new ReviewMasonry();
  }
});
